import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { registerDeviceToken } from "../store/auth";
import { requestForToken } from "../firebase";

export const FCM_STORAGE_KEY = "fcm_registered_token";

/**
 * Clears the cached "already registered" marker.
 * MUST be called on logout, otherwise the next login in the same tab
 * skips device-token registration entirely.
 */
export const clearFcmRegistration = () => {
  try {
    sessionStorage.removeItem(FCM_STORAGE_KEY);
    // legacy boolean flag from the previous implementation
    sessionStorage.removeItem("fcm_registered");
  } catch (e) {
    // sessionStorage unavailable (private mode) - nothing to clear
  }
};

/**
 * Single owner of FCM device-token registration.
 *
 * Runs whenever the logged-in user is (or becomes) a telecaller, and keeps
 * retrying on later renders until a token is actually registered. The cache
 * stores the registered token itself - not a boolean - so a rotated FCM token
 * or a fresh login re-hits the register API.
 */
const useFcmRegistration = () => {
  const dispatch = useDispatch();
  const { user, token: authToken } = useSelector((state) => state.auth);
  const inFlight = useRef(false);

  const isTelecaller = user?.selectedRole === "telecaller" || !!user?.is_tc;

  useEffect(() => {
    // Need an auth token: the register thunk sends it as the Bearer header.
    if (!isTelecaller || !authToken || inFlight.current) return;

    let cached = null;
    try {
      cached = sessionStorage.getItem(FCM_STORAGE_KEY);
    } catch (e) {
      cached = null;
    }

    // Re-register when the role changes too, not just when the FCM token rotates.
    const marker = (fcmToken) => `${user?.selectedRole || 'telecaller'}:${fcmToken}`;

    inFlight.current = true;
    requestForToken()
      .then((fcmToken) => {
        if (!fcmToken) {
          // Permission denied / no service worker / missing firebase env.
          // Leave nothing cached so the next mount tries again.
          return;
        }
        if (marker(fcmToken) === cached) return;

        return dispatch(registerDeviceToken({ token: fcmToken, platform: "web" }))
          .unwrap()
          .then(() => {
            try {
              sessionStorage.setItem(FCM_STORAGE_KEY, marker(fcmToken));
            } catch (e) {
              // ignore - registration still succeeded
            }
          });
      })
      .catch((err) => console.error("FCM device-token registration failed:", err))
      .finally(() => {
        inFlight.current = false;
      });
  }, [dispatch, isTelecaller, authToken, user?.selectedRole]);
};

export default useFcmRegistration;
